import { useEffect, useRef, useState } from "react";

/**
 * useInView — observes an element and reports visibility.
 * Pass once=true to fire only first time (default once=false so it retriggers).
 */
export function useInView({ threshold = 0.3, once = false, rootMargin = "0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          setInView(e.isIntersecting);
          if (e.isIntersecting && once) obs.disconnect();
        });
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once, threshold, rootMargin]);

  return [ref, inView];
}

/**
 * useCountUp — animates a number from 0 to `target` when `start` is true.
 * Resets to 0 whenever `start` flips to false, so it replays each time
 * the consumer toggles visibility.
 */
export function useCountUp(target, duration = 2000, start = false, decimals = 0) {
  const [value, setValue] = useState(0);
  const startTime = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);

    if (!start) {
      setValue(0);
      startTime.current = null;
      return undefined;
    }

    startTime.current = null;
    const step = (ts) => {
      if (startTime.current === null) startTime.current = ts;
      const elapsed = ts - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = target * eased;
      setValue(next);
      if (progress < 1) rafId.current = requestAnimationFrame(step);
    };
    rafId.current = requestAnimationFrame(step);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [target, duration, start]);

  if (decimals === 0) return Math.round(value);
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
